import React, { useRef } from "react";

import { useTranslation } from "react-i18next";

import ButtonTemplates from "./features/button/ButtonTemplates";
import AnnouncementsTemplates from "./features/announcements/AnnouncementsTemplates";
import CardsTemplates from "./features/cards/CardsTemplates";
import CountdownTemplates from "./features/countdown/CountdownTemplates";
import PanelsTemplates from "./features/panels/PanelsTemplates";
import ImageSliderTemplates from "./features/image-slider/ImageSliderTemplates";
import TooltipFootnoteTemplates from "./features/tooltip-footnote/TooltipFootnoteTemplates";
import AdvancedTabsGroupTemplates from "./features/advanced-tabs/group/AdvancedTabsGroupTemplates";
import PanelAdvancedTemplates from "./features/panel-advanced/PanelAdvancedTemplates";
import ColorPalettesPicker from "../../../../components/ui/color-palettes-picker/ColorPalettesPicker";
import {
  TemplatesActionButton,
  TemplatesCollapsibleContainer,
  TemplatesContainer,
  TemplatesContentContainer,
  TemplatesContentHeader,
  AnimationContainer,
  TemplatesContentHeading,
} from "./styles";
import { useOpticsStore } from "../../../../stores/use-optics-store";
import { MACRO_KEY } from "../../../defaults";
import { MACRO_WITH_COLOR_PALETTE, MACRO_WITH_TEMPLATES } from "./defaults";
import { useClickOutside } from "@Shared/Hooks/useClickOutside";
import { macroStores } from "../../../../stores/export-stores";
import NewsTemplates from "./features/news/NewsTemplates";
import PagesTemplates from "./features/pages/PagesTemplates";
import SpacesTemplates from "./features/spaces/SpacesTemplates";
import PeopleTemplates from "./features/people/PeopleTemplates";
import RecentlyActiveTemplates from "./features/recently-active/RecentlyActiveTemplates";
import UpcomingBirthdayTemplates from "./features/upcoming-birthdays/UpcomingBirthdayTemplates";
import NewStartersTemplates from "./features/new-starters/NewStartersTemplates";
import WorkAnniversaryTemplates from "./features/work-anniversary/WorkAnniversaryTemplates";

const templateComponents = {
  undefined: () => <></>,
  [MACRO_KEY.BUTTON]: ButtonTemplates,
  [MACRO_KEY.BUTTON_INLINE]: ButtonTemplates,
  [MACRO_KEY.ANNOUNCEMENTS]: AnnouncementsTemplates,
  [MACRO_KEY.CARDS]: CardsTemplates,
  [MACRO_KEY.COUNTDOWN]: CountdownTemplates,
  [MACRO_KEY.PANEL_SIMPLE]: PanelsTemplates,
  [MACRO_KEY.IMAGE_SLIDER]: ImageSliderTemplates,
  [MACRO_KEY.TOOLTIPS_AND_FOOTNOTES]: TooltipFootnoteTemplates,
  [MACRO_KEY.ADVANCED_TABS_GROUP]: AdvancedTabsGroupTemplates,
  [MACRO_KEY.PANEL_ADVANCED]: PanelAdvancedTemplates,
  [MACRO_KEY.NEWS]: NewsTemplates,
  [MACRO_KEY.PAGES]: PagesTemplates,
  [MACRO_KEY.SPACES]: SpacesTemplates,
  [MACRO_KEY.PEOPLE]: PeopleTemplates,
  [MACRO_KEY.RECENTLY_ACTIVE]: RecentlyActiveTemplates,
  [MACRO_KEY.UPCOMING_BIRTHDAYS]: UpcomingBirthdayTemplates,
  [MACRO_KEY.NEW_STARTERS]: NewStartersTemplates,
  [MACRO_KEY.WORK_ANNIVERSARY]: WorkAnniversaryTemplates,
};

const Templates = () => {
  const templatesDrawerRef = useRef();

  const { t } = useTranslation();
  const { drawers, setDrawers, macro, isDrawerModalOpen } = useOpticsStore(
    (state) => ({
      drawers: state.drawers,
      setDrawers: state.setDrawers,
      macro: state.macro,
      isDrawerModalOpen: state.isDrawerModalOpen,
    })
  );
  const macroData = macroStores[macro]?.((state) => state?.data || {});
  const hasMacroData = Array.isArray(macroData)
    ? !!macroData.length
    : !!Object.keys(macroData).length;

  const hasTemplates = MACRO_WITH_TEMPLATES.includes(macro);
  const hasColorPalette = MACRO_WITH_COLOR_PALETTE.includes(macro);

  const isCollapsed = !drawers.templates || !hasTemplates;

  const TemplateComponent = templateComponents[macro];

  useClickOutside(
    templatesDrawerRef,
    () => {
      if (drawers.templates && !isDrawerModalOpen) {
        setDrawers({ templates: false, settings: hasMacroData });
      }
    },
    "optics-sidebar"
  );

  return (
    <TemplatesCollapsibleContainer
      hasTemplates={hasTemplates}
      isCollapsed={isCollapsed}
      ref={templatesDrawerRef}
      macro={macro}
    >
      <TemplatesContainer
        isCollapsed={isCollapsed}
        onClick={() => {
          if (isCollapsed && hasTemplates) {
            setDrawers({
              templates: true,
              settings: false,
            });
          }
        }}
      >
        {hasTemplates && (
          <TemplatesActionButton
            onClick={() => {
              if (!isCollapsed) {
                setDrawers({ templates: false, settings: hasMacroData });
              }
            }}
          >
            {t("templates")}
          </TemplatesActionButton>
        )}

        <AnimationContainer isCollapsed={isCollapsed}>
          {drawers.templates && hasTemplates && (
            <TemplatesContentContainer>
              <TemplatesContentHeader>
                <TemplatesContentHeading>
                  {t("templates")}
                </TemplatesContentHeading>
                {hasColorPalette && <ColorPalettesPicker />}
              </TemplatesContentHeader>
              <TemplateComponent editorMacro={macro} />
            </TemplatesContentContainer>
          )}
        </AnimationContainer>
      </TemplatesContainer>
    </TemplatesCollapsibleContainer>
  );
};

export default Templates;
